import { ImageFileRecord } from "../../../types/files";
import { useRef, useState } from "react";
import { useAttachmentContext } from "../../../contexts/AttachmentContext";
import { Cancel01Icon, HelpCircleIcon } from "hugeicons-react";
import "../../../Styles/Layout/Input-Area/attachmentImageCard.css";
import { HoverToolTip } from "../Items/HoverToolTip";

interface ImageFileCardProps {
    imageFile: ImageFileRecord;
    onRemove?: () => void;
}

export const ImageAttachmentFileCard = ({ imageFile, onRemove }: ImageFileCardProps) => {
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const { removeImageFile, setImageModalState } = useAttachmentContext();

    // Prefer the remote URL once upload completes, otherwise fall back to the
    // transient object-URL preview populated in processImageFile.
    const imageUrl = imageFile.url || imageFile.previewUrl || '';

    const handleRemove = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        const el = containerRef.current;
        if (el) {
            const { width, height } = el.getBoundingClientRect();
            el.style.setProperty('--card-w', `${width}px`);
            el.style.setProperty('--card-h', `${height}px`);
        }
        setIsRemoving(true);
        
        setTimeout(() => {
            if (onRemove) {
                onRemove();
            } else {
                removeImageFile(imageFile.id);
            }
        }, 340);
    };

    const handleImageClick = () => {
        setImageModalState({
            isOpen: true,
            url: imageUrl,
            title: imageFile.name,
        });
    };

    return (
        <div
            ref={containerRef}
            className={`image-file-card-container ${isRemoving ? 'removing' : ''}`}
        >
            <HoverToolTip text="Click to see image" position="bottom" />
            {isLoading && !isError && (
                <div
                    className="image-loading-container"
                >
                    <span
                        className="loader-spinner"
                    />
                </div>
            )}
            {imageUrl && !isError ? (
                <img
                    src={imageUrl}
                    alt=""
                    className="image-element"
                    onLoad={() => setIsLoading(false)}
                    onError={() => {
                        setIsLoading(false);
                        setIsError(true);
                    }}
                    draggable={false}
                    onClick={handleImageClick}
                />
            ) : isError ? (
                <div
                    className="error-container"
                    title="Failed to load image"
                >
                   <HelpCircleIcon />
                </div>
            ) : null}
            <button
                type="button"
                aria-label="Remove image"
                onClick={handleRemove}
                className="remove-attachment-button"
                tabIndex={0}
            >
                <Cancel01Icon size={12} />
            </button>
        </div>
    );
};
